import { Building2, Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/common/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/common/ui/dialog";
import { Input } from "@/common/ui/input";
import { Label } from "@/common/ui/label";

import { createEnterprise } from "../api/organization-api";
import { slugFromName } from "../slug";

type Step = "form" | "creating" | "success";

interface CreateOrgModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOrgCreated: () => void;
}

export function CreateOrgModal({
  open,
  onOpenChange,
  onOrgCreated,
}: CreateOrgModalProps): React.ReactElement {
  const [step, setStep] = useState<Step>("form");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [createdName, setCreatedName] = useState("");
  const [createdSlug, setCreatedSlug] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setStep("form");
      setName("");
      setSlug("");
      setSlugTouched(false);
      setCreatedName("");
      setCreatedSlug("");
      setError("");
    }
  }, [open]);

  const handleNameChange = (value: string): void => {
    setName(value);
    if (!slugTouched) {
      setSlug(slugFromName(value));
    }
  };

  const handleSlugChange = (value: string): void => {
    setSlugTouched(true);
    setSlug(slugFromName(value));
  };

  const isValid = name.trim().length >= 2 && slug.trim().length >= 2;

  const handleCreate = async (): Promise<void> => {
    if (!isValid) return;

    setStep("creating");
    setError("");

    try {
      await createEnterprise({
        name: name.trim(),
        slug: slug.trim(),
      });
      setCreatedName(name.trim());
      setCreatedSlug(slug.trim());
      setStep("success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create organization");
      setStep("form");
    }
  };

  const handleDone = (): void => {
    onOpenChange(false);
    onOrgCreated();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onOpenChange(false);
      }}
    >
      <DialogContent className="sm:max-w-md">
        {step === "form" && (
          <>
            <DialogHeader>
              <DialogTitle>Create Organization</DialogTitle>
              <DialogDescription>
                Set up a new organization to manage your teams and members.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organization Name</Label>
                <Input
                  id="org-name"
                  placeholder="Acme Corporation"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-slug">URL Slug</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">/</span>
                  <Input
                    id="org-slug"
                    placeholder="acme-corp"
                    value={slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  This will be used in URLs and cannot be changed later.
                </p>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={!isValid}>
                Create Organization
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "creating" && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="mb-4 h-10 w-10 animate-spin text-primary" />
            <p className="text-sm font-medium">Creating your organization...</p>
            <p className="mt-1 text-xs text-muted-foreground">Setting up {name}</p>
          </div>
        )}

        {step === "success" && (
          <>
            <div className="flex flex-col items-center justify-center py-8">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                <Check className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h3 className="mb-1 text-lg font-semibold">Organization Created</h3>
              <p className="text-center text-sm text-muted-foreground">
                {createdName} is ready to use.
              </p>
            </div>
            <div className="mb-4 rounded-lg border bg-muted/50 p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{createdName}</p>
                  <p className="text-sm text-muted-foreground">/{createdSlug}</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleDone}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
